import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ArrowLeft, FileText, Shield, Users } from "lucide-react";
import { Link } from "react-router-dom";

export default function TermsOfServicePage() {
  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto py-8 px-6 max-w-4xl">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-6">
            <div className="w-16 h-16 bg-primary/10 rounded-2xl flex items-center justify-center">
              <FileText className="h-8 w-8 text-primary" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-foreground tracking-tight">Terms of Service</h1>
              <p className="text-muted-foreground mt-1">Last updated: January 15, 2025</p>
            </div>
          </div>
          <Link to="/login">
            <Button variant="outline" className="gap-2">
              <ArrowLeft className="h-4 w-4" />
              Back to Login
            </Button>
          </Link>
        </div>

        {/* Content */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-3">
              <Shield className="h-6 w-6 text-primary" />
              FeedbackPro Terms of Service
            </CardTitle>
          </CardHeader>
          <CardContent className="prose prose-gray max-w-none space-y-8">
            
            <section>
              <h2 className="text-xl font-semibold text-foreground mb-4">1. Acceptance of Terms</h2>
              <p className="text-muted-foreground leading-relaxed">
                By accessing or using FeedbackPro ("Service"), you agree to be bound by these Terms of Service ("Terms"). 
                If you disagree with any part of these terms, then you may not access the Service.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-foreground mb-4">2. Description of Service</h2>
              <p className="text-muted-foreground leading-relaxed mb-4">
                FeedbackPro is a team feedback and performance management platform that enables organizations to:
              </p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                <li>Collect and manage feedback between team members</li>
                <li>Set and track goals and objectives</li>
                <li>Conduct performance reviews and one-on-one meetings</li>
                <li>Generate analytics and reports on team performance</li>
                <li>Manage team structure and organizational hierarchy</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-foreground mb-4">3. User Accounts</h2>
              <div className="space-y-4">
                <div>
                  <h3 className="text-lg font-medium text-foreground mb-2">Account Registration</h3>
                  <p className="text-muted-foreground leading-relaxed">
                    You must provide accurate, current, and complete information during registration and keep your account information updated.
                  </p>
                </div>
                <div>
                  <h3 className="text-lg font-medium text-foreground mb-2">Account Security</h3>
                  <p className="text-muted-foreground leading-relaxed">
                    You are responsible for safeguarding your password and all activities under your account. 
                    Notify us immediately of any unauthorized use.
                  </p>
                </div>
                <div>
                  <h3 className="text-lg font-medium text-foreground mb-2">Account Termination</h3>
                  <p className="text-muted-foreground leading-relaxed">
                    We may terminate or suspend accounts that violate these Terms or engage in prohibited activities.
                  </p>
                </div>
              </div>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-foreground mb-4">4. Acceptable Use</h2>
              <div className="space-y-4">
                <p className="text-muted-foreground leading-relaxed">You agree not to use the Service to:</p>
                <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                  <li>Violate any applicable laws or regulations</li>
                  <li>Harass, abuse, or harm other users</li>
                  <li>Upload malicious content or software</li>
                  <li>Attempt to gain unauthorized access to our systems</li>
                  <li>Use the Service for any commercial purpose without authorization</li>
                  <li>Share inappropriate, offensive, or confidential content</li>
                </ul>
              </div>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-foreground mb-4">5. Data and Privacy</h2>
              <div className="space-y-4">
                <div>
                  <h3 className="text-lg font-medium text-foreground mb-2">Data Ownership</h3>
                  <p className="text-muted-foreground leading-relaxed">
                    You retain ownership of all data you submit to FeedbackPro. We process your data according to our Privacy Policy.
                  </p>
                </div>
                <div>
                  <h3 className="text-lg font-medium text-foreground mb-2">Data Security</h3>
                  <p className="text-muted-foreground leading-relaxed">
                    We implement industry-standard security measures to protect your data, including encryption, access controls, and regular security audits.
                  </p>
                </div>
                <div>
                  <h3 className="text-lg font-medium text-foreground mb-2">Data Retention</h3>
                  <p className="text-muted-foreground leading-relaxed">
                    We retain your data for as long as your account is active or as needed to provide services. 
                    You may request data deletion at any time.
                  </p>
                </div>
              </div>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-foreground mb-4">6. Subscription and Payment</h2>
              <div className="space-y-4">
                <div>
                  <h3 className="text-lg font-medium text-foreground mb-2">Billing</h3>
                  <p className="text-muted-foreground leading-relaxed">
                    Subscription fees are billed in advance on a monthly or annual basis. All fees are non-refundable except as required by law.
                  </p>
                </div>
                <div>
                  <h3 className="text-lg font-medium text-foreground mb-2">Plan Changes</h3>
                  <p className="text-muted-foreground leading-relaxed">
                    You may upgrade or downgrade your subscription at any time. Changes take effect at the next billing cycle.
                  </p>
                </div>
                <div>
                  <h3 className="text-lg font-medium text-foreground mb-2">Cancellation</h3>
                  <p className="text-muted-foreground leading-relaxed">
                    You may cancel your subscription at any time. Your account will remain active until the end of the current billing period.
                  </p>
                </div>
              </div>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-foreground mb-4">7. Intellectual Property</h2>
              <div className="space-y-4">
                <div>
                  <h3 className="text-lg font-medium text-foreground mb-2">Service Ownership</h3>
                  <p className="text-muted-foreground leading-relaxed">
                    FeedbackPro and its original content, features, and functionality are owned by our company and protected by copyright, trademark, and other laws.
                  </p>
                </div>
                <div>
                  <h3 className="text-lg font-medium text-foreground mb-2">User Content</h3>
                  <p className="text-muted-foreground leading-relaxed">
                    You grant us a license to use, store, and process your content to provide the Service. 
                    This license ends when you delete your content or account.
                  </p>
                </div>
              </div>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-foreground mb-4">8. Limitation of Liability</h2>
              <p className="text-muted-foreground leading-relaxed">
                To the maximum extent permitted by law, FeedbackPro shall not be liable for any indirect, incidental, 
                special, consequential, or punitive damages, including but not limited to loss of profits, data, or other intangible losses.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-foreground mb-4">9. Disclaimers</h2>
              <p className="text-muted-foreground leading-relaxed">
                The Service is provided "as is" and "as available" without warranties of any kind. 
                We do not guarantee that the Service will be uninterrupted, secure, or error-free.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-foreground mb-4">10. Changes to Terms</h2>
              <p className="text-muted-foreground leading-relaxed">
                We reserve the right to modify these Terms at any time. We will notify users of significant changes 
                via email or through the Service. Continued use constitutes acceptance of the modified Terms.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-foreground mb-4">11. Governing Law</h2>
              <p className="text-muted-foreground leading-relaxed">
                These Terms are governed by and construed in accordance with the laws of [Your Jurisdiction], 
                without regard to conflict of law principles.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-foreground mb-4">12. Contact Information</h2>
              <p className="text-muted-foreground leading-relaxed">
                If you have any questions about these Terms of Service, please contact us at:
              </p>
              <div className="mt-4 p-4 bg-muted rounded-lg">
                <p className="text-muted-foreground">
                  <strong>Email:</strong> legal@feedbackpro.com<br />
                  <strong>Address:</strong> [Your Company Address]<br />
                  <strong>Phone:</strong> [Your Phone Number]
                </p>
              </div>
            </section>

          </CardContent>
        </Card>

        {/* Footer Actions */}
        <div className="mt-8 flex justify-center gap-4">
          <Link to="/privacy">
            <Button variant="outline" className="gap-2">
              <Users className="h-4 w-4" />
              Privacy Policy
            </Button>
          </Link>
          <Link to="/login">
            <Button className="gap-2">
              <ArrowLeft className="h-4 w-4" />
              Back to Login
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
}
